import type {
  Coupon,
  CouponTarget,
  GetBillingAccountOverviewResponse,
  ListCouponsResponse,
} from "@nlc/shared-types";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { subscriptionKeys } from "@/hooks/queryKeys";

import { addCoupon } from "../utils/account-overview-cache";

const MOCK_COUPONS: ListCouponsResponse = [
  {
    code: "GS_20_ONCE",
    discountLabel: "20% off",
    durationLabel: "Once",
    id: "gs_20_once",
  },
  {
    code: "MKTN_20_3MO",
    discountLabel: "20% off",
    durationLabel: "For 3 months",
    id: "mktn_20_3mo",
  },
  {
    code: "AFFL_10_ALWAYS",
    discountLabel: "10% off",
    durationLabel: "Always",
    id: "affl_10_always",
  },
];

export function useCoupons(): UseQueryResult<ListCouponsResponse> {
  return useQuery<ListCouponsResponse>({
    queryFn: () => MOCK_COUPONS,
    queryKey: subscriptionKeys.coupons(),
    staleTime: Infinity,
  });
}

export function useAddAccountCoupon(
  projectId: number,
): UseMutationResult<Coupon, Error, { coupon: Coupon; target: CouponTarget }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ coupon }: { coupon: Coupon; target: CouponTarget }) =>
      coupon,
    onSuccess: (coupon) => {
      queryClient.setQueryData<GetBillingAccountOverviewResponse>(
        subscriptionKeys.accountOverview(projectId),
        (overview) => overview && addCoupon(overview, coupon),
      );

      toast.add({
        description: `${coupon.code} will apply starting on the next invoice.`,
        timeout: 3000,
        title: "Coupon added",
        type: "success",
      });
    },
  });
}
